import { lazy, Suspense, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { AuthGuard } from '@/components/AuthGuard';
import { AdminGuard } from '@/components/AdminGuard';
import { CommandPalette } from '@/components/CommandPalette';
import { UpgradeModal } from '@/components/billing/UpgradeModal';
import { startAutomationRunner } from '@/lib/automationRunner';

// ===== Public =====
const LandingPage   = lazy(() => import('@/pages/LandingPage'));
const LoginPage     = lazy(() => import('@/pages/LoginPage'));
const RegisterPage  = lazy(() => import('@/pages/RegisterPage'));
const ForgotPasswordPage = lazy(() => import('@/pages/ForgotPasswordPage'));
const ResetPasswordPage  = lazy(() => import('@/pages/ResetPasswordPage'));
const PricingPage   = lazy(() => import('@/pages/PricingPage'));
const FeaturesPage  = lazy(() => import('@/pages/FeaturesPage'));
const PublicFormPage = lazy(() => import('@/pages/PublicFormPage'));

// ===== Growth / SEO =====
const TemplateGalleryPage = lazy(() => import('@/pages/TemplateGalleryPage'));
const TemplateDetailPage  = lazy(() => import('@/pages/TemplateDetailPage'));
const BlogIndexPage       = lazy(() => import('@/pages/BlogIndexPage'));
const BlogPostPage        = lazy(() => import('@/pages/BlogPostPage'));
const ComparisonPage      = lazy(() => import('@/pages/ComparisonPage'));
const AboutPage           = lazy(() => import('@/pages/AboutPage'));
const ContactPage         = lazy(() => import('@/pages/ContactPage'));
const LegalPage           = lazy(() => import('@/pages/LegalPage'));

// ===== App =====
const DashboardPage       = lazy(() => import('@/pages/DashboardPage'));
const BoardPage           = lazy(() => import('@/pages/BoardPage'));
const MyWorkPage          = lazy(() => import('@/pages/MyWorkPage'));
const InboxPage           = lazy(() => import('@/pages/InboxPage'));
const PeoplePage          = lazy(() => import('@/pages/PeoplePage'));
const FavoritesPage       = lazy(() => import('@/pages/FavoritesPage'));
const RecentPage          = lazy(() => import('@/pages/RecentPage'));
const DashboardsPage      = lazy(() => import('@/pages/DashboardsPage'));
const DashboardDetailPage = lazy(() => import('@/pages/DashboardDetailPage'));
const WorkloadPage        = lazy(() => import('@/pages/WorkloadPage'));
const FormsPage           = lazy(() => import('@/pages/FormsPage'));
const FormBuilderPage     = lazy(() => import('@/pages/FormBuilderPage'));
const DocsPage            = lazy(() => import('@/pages/DocsPage'));
const DocEditorPage       = lazy(() => import('@/pages/DocEditorPage'));
const AutomationsPage     = lazy(() => import('@/pages/AutomationsPage'));
const IntegrationsPage    = lazy(() => import('@/pages/IntegrationsPage'));
const AiStudioPage        = lazy(() => import('@/pages/AiStudioPage'));
const BillingPage         = lazy(() => import('@/pages/BillingPage'));
const SettingsPage        = lazy(() => import('@/pages/SettingsPage'));
const AdminPage           = lazy(() => import('@/pages/AdminPage'));

const WelcomePage   = lazy(() => import('@/pages/WelcomePage'));
const NotFoundPage  = lazy(() => import('@/pages/NotFoundPage'));

function PageSpinner() {
  return (
    <div className="flex h-screen items-center justify-center">
      <div className="h-12 w-12 rounded-2xl bg-gradient-myday animate-pulse-slow" />
    </div>
  );
}

export default function App() {
  const init = useAuthStore((s) => s.init);
  useEffect(() => { void init(); }, [init]);
  useEffect(() => { startAutomationRunner(); }, []);

  return (
    <Suspense fallback={<PageSpinner />}>
      <CommandPalette />
      <UpgradeModal />
      <Routes>
        {/* Public */}
        <Route path="/"         element={<LandingPage />} />
        <Route path="/login"           element={<LoginPage />} />
        <Route path="/register"        element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password"  element={<ResetPasswordPage />} />
        <Route path="/pricing"  element={<PricingPage />} />
        <Route path="/features" element={<FeaturesPage />} />
        <Route path="/f/:slug"  element={<PublicFormPage />} />

        {/* Growth / SEO */}
        <Route path="/templates"         element={<TemplateGalleryPage />} />
        <Route path="/templates/:slug"   element={<TemplateDetailPage />} />
        <Route path="/blog"              element={<BlogIndexPage />} />
        <Route path="/blog/:slug"        element={<BlogPostPage />} />
        <Route path="/vs/:slug"          element={<ComparisonPage />} />
        <Route path="/about"             element={<AboutPage />} />
        <Route path="/contact"           element={<ContactPage />} />
        <Route path="/privacy"           element={<LegalPage />} />
        <Route path="/terms"             element={<LegalPage />} />
        <Route path="/security"          element={<LegalPage />} />
        <Route path="/dpa"               element={<LegalPage />} />
        <Route path="/careers"           element={<LegalPage />} />

        {/* Onboarding */}
        <Route path="/welcome"   element={<AuthGuard><WelcomePage /></AuthGuard>} />

        {/* App — core */}
        <Route path="/boards"    element={<AuthGuard><DashboardPage /></AuthGuard>} />
        <Route path="/board/:id" element={<AuthGuard><BoardPage /></AuthGuard>} />
        <Route path="/my-work"   element={<AuthGuard><MyWorkPage /></AuthGuard>} />
        <Route path="/inbox"     element={<AuthGuard><InboxPage /></AuthGuard>} />
        <Route path="/people"    element={<AuthGuard><PeoplePage /></AuthGuard>} />
        <Route path="/favorites" element={<AuthGuard><FavoritesPage /></AuthGuard>} />
        <Route path="/recent"    element={<AuthGuard><RecentPage /></AuthGuard>} />

        {/* App — Phase 3 */}
        <Route path="/dashboards"      element={<AuthGuard><DashboardsPage /></AuthGuard>} />
        <Route path="/dashboards/:id"  element={<AuthGuard><DashboardDetailPage /></AuthGuard>} />
        <Route path="/workload"        element={<AuthGuard><WorkloadPage /></AuthGuard>} />
        <Route path="/forms"           element={<AuthGuard><FormsPage /></AuthGuard>} />
        <Route path="/forms/:id"       element={<AuthGuard><FormBuilderPage /></AuthGuard>} />
        <Route path="/docs"            element={<AuthGuard><DocsPage /></AuthGuard>} />
        <Route path="/docs/:id"        element={<AuthGuard><DocEditorPage /></AuthGuard>} />

        {/* App — Phase 4 */}
        <Route path="/automations"  element={<AuthGuard><AutomationsPage /></AuthGuard>} />
        <Route path="/integrations" element={<AuthGuard><IntegrationsPage /></AuthGuard>} />

        {/* App — Phase 5 */}
        <Route path="/ai"           element={<AuthGuard><AiStudioPage /></AuthGuard>} />

        {/* App — Phase 7 */}
        <Route path="/billing"      element={<AuthGuard><BillingPage /></AuthGuard>} />
        <Route path="/settings"     element={<AuthGuard><SettingsPage /></AuthGuard>} />
        <Route path="/admin"        element={<AuthGuard><AdminGuard><AdminPage /></AdminGuard></AuthGuard>} />

        {/* Aliases */}
        <Route path="/dashboard" element={<Navigate to="/boards" replace />} />

        {/* 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}
